import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Collapse, IconButton, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DeliveryNoteBatchStatus, RecentDeliveryNoteBatch } from 'models/order';
import { describeFailedOrders } from 'components/orders/shared/use-delivery-note-batch';
import { downloadUrl as triggerDownload } from 'utils/file';
import { formatDate } from 'utils/format/format-date';

type RecentBatchRowProps = {
    batch: RecentDeliveryNoteBatch;
    onRerun: (batchId: string) => void;
    isRerunning: boolean;
};

const isGenerating = (status: RecentDeliveryNoteBatch['status']): boolean =>
    status === DeliveryNoteBatchStatus.PROCESSING || status === DeliveryNoteBatchStatus.MERGING;

const isRerunnable = (status: RecentDeliveryNoteBatch['status']): boolean =>
    status === DeliveryNoteBatchStatus.FAILED || status === DeliveryNoteBatchStatus.PARTIAL;

// Text + color, like order-status-chip: color alone must not carry the status.
const statusColorMap: Record<DeliveryNoteBatchStatus, string> = {
    [DeliveryNoteBatchStatus.PROCESSING]: 'focus.dark',
    [DeliveryNoteBatchStatus.MERGING]: 'focus.dark',
    [DeliveryNoteBatchStatus.COMPLETED]: 'success.main',
    [DeliveryNoteBatchStatus.PARTIAL]: 'warning.dark',
    [DeliveryNoteBatchStatus.FAILED]: 'error.dark',
};

const statusLabelKeyMap: Record<DeliveryNoteBatchStatus, string> = {
    [DeliveryNoteBatchStatus.PROCESSING]: 'orders:BATCH_STATUS_GENERATING',
    [DeliveryNoteBatchStatus.MERGING]: 'orders:BATCH_STATUS_GENERATING',
    [DeliveryNoteBatchStatus.COMPLETED]: 'orders:BATCH_STATUS_COMPLETED',
    [DeliveryNoteBatchStatus.PARTIAL]: 'orders:BATCH_STATUS_PARTIAL',
    [DeliveryNoteBatchStatus.FAILED]: 'orders:BATCH_STATUS_FAILED',
};

/**
 * DD-3592: one batch in the recent-batches list — when and what it was, how it
 * went, the compiled PDF when there is one, and a re-run for failed orders.
 */
const RecentBatchRow: FC<RecentBatchRowProps> = ({ batch, onRerun, isRerunning }: RecentBatchRowProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [isFailedListOpen, setIsFailedListOpen] = useState(false);

    const actionLabel = batch.action ? translate(`orders:BATCH_ACTION_${batch.action}`) : '';
    const rerunSuffix = batch.isRerun ? ` ${translate('orders:BATCH_RERUN_SUFFIX')}` : '';

    const statusChip = () => {
        if (!batch.status) {
            return null;
        }
        return (
            <Box display="flex" alignItems="center" gap={0.5}>
                {isGenerating(batch.status) ? <CircularProgress size={14} /> : undefined}
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: statusColorMap[batch.status] }}
                    data-test="recent-batch-row.status"
                >
                    {translate(statusLabelKeyMap[batch.status])}
                </Typography>
            </Box>
        );
    };

    const download = () => {
        triggerDownload(`delivery-notes-${batch._id}.pdf`, batch.downloadUrl as string);
    };

    return (
        <Box py={1} data-test="recent-batch-row">
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {actionLabel}
                        {rerunSuffix}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {formatDate(batch.createdAt)}
                    </Typography>
                </Box>
                {statusChip()}
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mt={0.5}>
                <Box display="flex" alignItems="center">
                    <Typography variant="body2" data-test="recent-batch-row.counts">
                        {`${batch.completedOrders.length}/${batch.total}`}
                    </Typography>
                    {batch.failedOrders.length ? (
                        <IconButton
                            size="small"
                            aria-label={translate('orders:BATCH_FAILED_ORDERS')}
                            onClick={() => setIsFailedListOpen((wasOpen) => !wasOpen)}
                            data-test="recent-batch-row.toggle-failed"
                        >
                            <ExpandMoreIcon
                                fontSize="small"
                                sx={{ transform: isFailedListOpen ? 'rotate(180deg)' : 'none' }}
                            />
                        </IconButton>
                    ) : undefined}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    {batch.downloadUrl ? (
                        <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={download}
                            data-test="recent-batch-row.download"
                        >
                            {translate('orders:BATCH_DOWNLOAD_PDF')}
                        </Button>
                    ) : undefined}
                    {isRerunnable(batch.status) ? (
                        <Button
                            size="small"
                            color="warning"
                            startIcon={<ReplayIcon />}
                            disabled={isRerunning}
                            onClick={() => onRerun(batch._id)}
                            data-test="recent-batch-row.rerun"
                        >
                            {translate('orders:BATCH_RERUN')}
                        </Button>
                    ) : undefined}
                </Box>
            </Box>
            <Collapse in={isFailedListOpen}>
                <Typography variant="caption" color="error.dark" data-test="recent-batch-row.failed-orders">
                    {`${translate('orders:BATCH_FAILED_ORDERS')}: ${describeFailedOrders(batch.failedOrders)}`}
                </Typography>
            </Collapse>
        </Box>
    );
};

export default RecentBatchRow;
